/**
 * @file Shared HTTP route contracts for `lab`'s **lab-specific**
 *       account surface — the `/me` response shape with the
 *       lab-only `currentChapter` join, plus the two lab-only routes
 *       (`/account/me/chapter`, `/account/update`).
 * @module @labcraft/lab/shared/account-routes
 *
 * The provider-shaped routes (`/account/google/{signin,callback}`,
 * `/account/github/{signin,callback}`,
 * `/account/email/{signin,callback}`) plus `/account/signout` are
 * inherited from `@labcraft/account/proxy-routes` via the
 * `Proxy` superclass — they're identical across every consumer, so
 * we don't re-declare them here. Consumers that need typed access to
 * those (lab's UI, integration tests) import them from
 * `@labcraft/account/proxy-routes` directly.
 *
 * What remains here:
 *
 *   - `MeRoute` / `MeResponseSchema` — lab tightens the base proxy's
 *     `.passthrough()` learner shape with the explicit
 *     `currentChapter: ChapterRefSchema.nullable()` field that lab
 *     joins from `learner_progress` in its `lookup()` override.
 *   - `SetChapterRoute` — lab-only; persists the chapter pointer.
 *   - `UpdateAccountRoute` — lab-only; forwards to account-service's
 *     `updateProfile` then refreshes the lab-side session cache.
 *
 * All paths are service-tree-relative starting with the owning
 * service's id (`/account`). `Lab` prepends `/api`, so the public
 * URLs end up at `/api/account/*`.
 */

import { z } from 'zod'
import { AcquisitionSchema, defineRoute } from '@labcraft/core'
import { ChapterRefSchema } from './ws_messages.ts'

////////////////////////////////////////////////////////////////////////////////
// /account/me
////////////////////////////////////////////////////////////////////////////////

/**
 * `GET /account/me` response shape. Returns the authenticated
 * learner when the session cookie is valid; `{ learner: null }` for
 * anonymous requests. Never errors — the SPA reads it on every page
 * load to determine auth state.
 *
 * `currentChapter` is the account-global pointer (ADR-006 (g)). Lab
 * joins `learner_progress` for this field so the SPA sees it
 * alongside the account-service-sourced learner data (lab caches the
 * rest in the session payload at signin time so /me reads don't
 * round-trip account-service on the hot path).
 *
 * `status` / `profileCompletedAt` / `avatarUrl` are the access +
 * profile + provider-pic fields the SPA's `AuthGuard` uses to route
 * the learner to `/account/inactive`, `/account/update`, or onward.
 *
 * `acquisition` is the captured acquisition attribution (`source` +
 * `category`), or `null` when never captured; the SPA flattens it into
 * `umami.identify()` (umbrella #1213, Phase 2). `null` for rows that
 * predate capture or had nothing to classify.
 *
 * `createdAt` is the learner's signup instant; the SPA buckets it to a
 * `YYYY-Www` cohort week for `umami.identify()` (umbrella #1213, Phase 2).
 * The raw timestamp never reaches Umami — only the derived bucket.
 *
 * @property learner - Authenticated learner info, or `null` when
 *   anonymous.
 */
export const MeResponseSchema = z.object({
  learner: z
    .object({
      id: z.string().uuid(),
      email: z.string().email(),
      displayName: z.string(),
      emailVerifiedAt: z.string().datetime().nullable(),
      createdAt: z.string().datetime(),
      currentChapter: ChapterRefSchema.nullable(),
      status: z.enum(['active', 'inactive', 'banned']),
      profileCompletedAt: z.string().datetime().nullable(),
      avatarUrl: z.string().url().nullable(),
      acquisition: AcquisitionSchema.nullable(),
    })
    .nullable(),
})

/** Parsed `MeResponse`. */
export type MeResponse = z.infer<typeof MeResponseSchema>

/** `GET /account/me` — returns the current learner or `null`. */
export const MeRoute = defineRoute({
  method: 'GET',
  path: '/api/account/me',
  response: MeResponseSchema,
  description:
    'Return the current authenticated learner, or `{ learner: null }` for anonymous requests.',
})

////////////////////////////////////////////////////////////////////////////////
// /account/me/chapter
////////////////////////////////////////////////////////////////////////////////

/**
 * `POST /account/me/chapter` request body — the new chapter pointer.
 * Persisted to `learner_progress.current_chapter`. Write-only today —
 * the cross-tab `chapter` broadcast (so sibling tabs follow) is an
 * unbuilt ADR-006 (g) follow-up; #1060 corrected this doc, which
 * previously described a broadcast the handler never performed and
 * retired the producer-less `chapter` frame that presupposed it.
 */
export const SetChapterRequestSchema = ChapterRefSchema

/** Parsed `POST /account/me/chapter` body. */
export type SetChapterRequest = z.infer<typeof SetChapterRequestSchema>

/**
 * `POST /account/me/chapter` response.
 *
 * `gated` is the soft-gate signal (umbrella #1357, DAG-correct gate):
 * when the requested pointer would advance into a Part whose
 * prerequisite Part assessment the learner hasn't passed yet, the handler
 * leaves `learner_progress.current_chapter` unchanged and returns
 * `{ ok: true, gated: true }`. The SPA reads `gated` to decide whether
 * to patch its cached `currentChapter` — a gated write must not, or the
 * cache would lie about where the pointer sits. The advance is *soft*:
 * the learner can still load the target chapter (no hard server-side
 * "can't load the next Part until the prior Part's assessment passed" —
 * that's a later entitlements follow-up); only the persisted pointer is
 * held back. `gated` is absent (undefined) on an accepted write.
 *
 * @property ok - always `true` — the request was well-formed and
 *   handled (a refused advance is still a successful call).
 * @property gated - `true` iff the pointer advance was held back by the
 *   soft gate; absent on an accepted write.
 */
export const SetChapterResponseSchema = z.object({
  ok: z.literal(true),
  gated: z.boolean().optional(),
})

/** Parsed `POST /account/me/chapter` response. */
export type SetChapterResponse = z.infer<typeof SetChapterResponseSchema>

/**
 * `POST /account/me/chapter` — set the learner's account-global
 * chapter pointer (ADR-006 (g)). Authenticated; 401 on
 * missing/invalid cookie. Persists to `learner_progress` (lab-domain),
 * subject to the umbrella #1357 soft gate: a forward advance into a Part
 * whose prerequisite Part assessment the learner hasn't passed is held
 * back (the response's `gated: true`), the pointer left where it was.
 * Backward / resume navigation and advances within a Part (or into a Part
 * whose prerequisite assessments are all passed) are never gated. The
 * cross-tab broadcast is a follow-up (see the request-schema note).
 */
export const SetChapterRoute = defineRoute({
  method: 'POST',
  path: '/api/account/me/chapter',
  request: SetChapterRequestSchema,
  response: SetChapterResponseSchema,
  description:
    "Set the learner's account-global chapter pointer (soft-gated on the prerequisite Part assessment).",
})

////////////////////////////////////////////////////////////////////////////////
// /account/me/part-assessment
////////////////////////////////////////////////////////////////////////////////

/**
 * `POST /account/me/part-assessment` request body — a recorded Part-level
 * assessment pass (umbrella #1357). Persisted to `learner_part_assessment`
 * as the one-row-per-Part terminal pass the soft gate reads. `score` is
 * the weighted points the engine banked at pass time (`earned`; may be
 * fractional after the prior-wrong discount); `band` is the optional
 * terminal CAT difficulty band label. Only the terminal pass rides this
 * route — per-attempt data goes to the analytics funnel, not here, and an
 * in-progress assessment is never persisted.
 *
 * @property courseId - Course the Part belongs to.
 * @property partId - Part whose assessment was passed.
 * @property score - Weighted points banked at pass time.
 * @property band - Optional terminal CAT band label at pass time.
 */
export const RecordPartAssessmentRequestSchema = z.object({
  courseId: z.string().min(1),
  partId: z.string().min(1),
  score: z.number().nonnegative(),
  band: z.string().min(1).optional(),
})

/** Parsed `POST /account/me/part-assessment` body. */
export type RecordPartAssessmentRequest = z.infer<
  typeof RecordPartAssessmentRequestSchema
>

/** `POST /account/me/part-assessment` response — empty acknowledgement. */
export const RecordPartAssessmentResponseSchema = z.object({
  ok: z.literal(true),
})

/** Parsed `POST /account/me/part-assessment` response. */
export type RecordPartAssessmentResponse = z.infer<
  typeof RecordPartAssessmentResponseSchema
>

/**
 * `POST /account/me/part-assessment` — record the learner's terminal
 * Part-level assessment pass (umbrella #1357). Authenticated; 401 on
 * missing/invalid cookie. Upserts `learner_part_assessment` (lab-domain)
 * so a re-pass refreshes the row. This is the soft gate's write side —
 * the Part-assessment page calls it on the CAT's `pass` verdict;
 * `handleSetChapter` reads the row on the next pointer advance into a
 * Part that lists this Part as a prerequisite.
 */
export const RecordPartAssessmentRoute = defineRoute({
  method: 'POST',
  path: '/api/account/me/part-assessment',
  request: RecordPartAssessmentRequestSchema,
  response: RecordPartAssessmentResponseSchema,
  description: "Record the learner's Part-level assessment pass.",
})

////////////////////////////////////////////////////////////////////////////////
// /account/me/objective-complete — per-objective completion (#1493)
////////////////////////////////////////////////////////////////////////////////

/**
 * `POST /account/me/objective-complete` request body — one objective the
 * learner just completed, identified by its full location + authored id
 * (#1493). Upserts a `learner_objective_progress` row so the chapter's
 * checkmarks survive a VM resume / reload; the write is idempotent, so a
 * re-run of the satisfying command records nothing new.
 *
 * @property courseId - Course the objective's chapter belongs to.
 * @property partId - Part the chapter belongs to.
 * @property chapterId - Chapter the objective belongs to.
 * @property objectiveId - The completed objective's authored id.
 */
export const RecordObjectiveCompleteRequestSchema = z.object({
  courseId: z.string().min(1),
  partId: z.string().min(1),
  chapterId: z.string().min(1),
  objectiveId: z.string().min(1),
})

/** Parsed `POST /account/me/objective-complete` body. */
export type RecordObjectiveCompleteRequest = z.infer<
  typeof RecordObjectiveCompleteRequestSchema
>

/** `POST /account/me/objective-complete` response — empty acknowledgement. */
export const RecordObjectiveCompleteResponseSchema = z.object({
  ok: z.literal(true),
})

/** Parsed `POST /account/me/objective-complete` response. */
export type RecordObjectiveCompleteResponse = z.infer<
  typeof RecordObjectiveCompleteResponseSchema
>

/**
 * `POST /account/me/objective-complete` — record the learner's completion
 * of one chapter objective (#1493). Authenticated; 401 on missing/invalid
 * cookie. Idempotent upsert into `learner_objective_progress`; the SPA
 * fires it fire-and-forget on the reducer's `objective.completed`, and the
 * chapter mount reads the set back to rehydrate the checkmarks after a VM
 * resume or reload.
 */
export const RecordObjectiveCompleteRoute = defineRoute({
  method: 'POST',
  path: '/api/account/me/objective-complete',
  request: RecordObjectiveCompleteRequestSchema,
  response: RecordObjectiveCompleteResponseSchema,
  description: "Record the learner's completion of one chapter objective.",
})

////////////////////////////////////////////////////////////////////////////////
// /account/me/chapter/complete — authoritative server-side completion record
////////////////////////////////////////////////////////////////////////////////

/**
 * `POST /account/me/chapter/complete` request body — the chapter the
 * learner just completed. Identifies the chapter by its full
 * `(courseId, partId, chapterId)` ref so the server-side
 * `progress_chapter_complete` Umami event (#1218) carries the same
 * triple the client `reader_chapter_complete` does.
 *
 * This is the authoritative, blocker-proof completion record: the SPA
 * POSTs here when a chapter's objectives all clear, alongside the
 * (blockable) client `track('reader_chapter_complete')`. The two form a
 * deliberate pair — the server count is un-spoofable and survives content
 * blockers; the delta between them measures the blind-rate.
 */
export const CompleteChapterRequestSchema = ChapterRefSchema

/** Parsed `POST /account/me/chapter/complete` body. */
export type CompleteChapterRequest = z.infer<
  typeof CompleteChapterRequestSchema
>

/** `POST /account/me/chapter/complete` response — empty acknowledgement. */
export const CompleteChapterResponseSchema = z.object({ ok: z.literal(true) })

/** Parsed `POST /account/me/chapter/complete` response. */
export type CompleteChapterResponse = z.infer<
  typeof CompleteChapterResponseSchema
>

/**
 * `POST /account/me/chapter/complete` — record a chapter completion
 * server-side (authoritative) and fire the blocker-proof
 * `progress_chapter_complete` Umami event with the learner UUID as the
 * distinct id (#1218). Authenticated; 401 on missing/invalid cookie.
 *
 * The record is fire-and-forget for analytics purposes — the response
 * acknowledges receipt; the Umami send is best-effort and never blocks or
 * fails the request. Idempotency is intentionally not enforced here: a
 * learner re-clearing a chapter is a legitimate re-completion, and the
 * Umami count is a behavioral funnel metric, not a source-of-truth count
 * (Postgres owns those, per the umbrella's division of labor).
 */
export const CompleteChapterRoute = defineRoute({
  method: 'POST',
  path: '/api/account/me/chapter/complete',
  request: CompleteChapterRequestSchema,
  response: CompleteChapterResponseSchema,
  description:
    'Record an authoritative server-side chapter completion (fires progress_chapter_complete).',
})

////////////////////////////////////////////////////////////////////////////////
// /account/update
////////////////////////////////////////////////////////////////////////////////

/**
 * Body of `POST /account/update`. Mutates the learner-mutable
 * profile fields. Currently just `displayName`; future profile
 * questions extend this shape without renaming the route.
 *
 * @property displayName - Learner-chosen display name (1–80 chars,
 *   already trimmed by the SPA).
 */
export const UpdateAccountRequestSchema = z.object({
  displayName: z.string().min(1).max(80),
})

/** Parsed `UpdateAccountRequest`. */
export type UpdateAccountRequest = z.infer<typeof UpdateAccountRequestSchema>

/**
 * `POST /account/update` response — same `{ learner }` shape as
 * `/account/me` so the SPA can drop the response straight into its
 * cached learner state. Returns the updated learner including the
 * freshly-stamped `profileCompletedAt`.
 */
export const UpdateAccountResponseSchema = MeResponseSchema.extend({
  learner: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string(),
    emailVerifiedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    currentChapter: ChapterRefSchema.nullable(),
    status: z.enum(['active', 'inactive', 'banned']),
    profileCompletedAt: z.string().datetime().nullable(),
    avatarUrl: z.string().url().nullable(),
    acquisition: AcquisitionSchema.nullable(),
  }),
})

/** Parsed `UpdateAccountResponse`. */
export type UpdateAccountResponse = z.infer<typeof UpdateAccountResponseSchema>

/**
 * `POST /account/update` — update the learner's profile fields.
 * Idempotent on `profile_completed_at` (set on first call; preserved
 * on subsequent edits). Requires an authenticated session AND
 * `status === 'active'`. 401 for anonymous; 403 for inactive /
 * banned. The SPA's `AuthGuard` should prevent inactive learners
 * from reaching this page in the first place; the backend check is
 * defense in depth.
 */
export const UpdateAccountRoute = defineRoute({
  method: 'POST',
  path: '/api/account/update',
  request: UpdateAccountRequestSchema,
  response: UpdateAccountResponseSchema,
  description: 'Update the authenticated learner’s profile fields.',
})

////////////////////////////////////////////////////////////////////////////////
// /account/me/budget — daily VM-time budget for the chrome meter (#941)
////////////////////////////////////////////////////////////////////////////////

/**
 * `GET /account/me/budget` response — the session learner's daily
 * VM-time budget plus the upgrade hand-off URL. The SPA's chrome meter
 * (#941) reads this: `tier` gates the meter's presence (paid → no
 * meter), `usedMsToday` / `limitMs` drive the remaining-time ring, and
 * `upgradeUrl` is the "go unlimited" target shared with the
 * `budget-exhausted` WS frame.
 *
 * Lab tightens `@labcraft/account`'s `BudgetSchema` rather than
 * re-exporting it (same reason `MeResponseSchema` is lab-owned — the
 * lab surface owns its wire contract) and layers `upgradeUrl` on top,
 * which account-service doesn't know about (it's a lab deploy-shape
 * value, `${publicUrl}/upgrade`).
 *
 * The reset time is intentionally absent: it's a pure function of the
 * clock (the next UTC-day boundary), so the SPA computes the concrete
 * local reset time itself rather than plumbing it through the wire.
 *
 * @property tier - `'free'` (metered) or `'paid'` (unlimited; no meter).
 * @property usedMsToday - `running` wall-clock ms used this UTC day.
 * @property limitMs - Free-tier daily cap in ms, or `null` for paid.
 * @property upgradeUrl - The "go unlimited" hand-off URL.
 */
export const BudgetResponseSchema = z.object({
  tier: z.enum(['free', 'paid']),
  usedMsToday: z.number().int().nonnegative(),
  limitMs: z.number().int().nonnegative().nullable(),
  upgradeUrl: z.string().url(),
})

/** Parsed `BudgetResponse`. */
export type BudgetResponse = z.infer<typeof BudgetResponseSchema>

/**
 * `GET /account/me/budget` — the session learner's daily VM-time
 * budget + upgrade URL for the chrome meter (#941). Authenticated; 401
 * on missing/invalid cookie. Lab resolves the session learner from the
 * cookie and forwards to `@labcraft/account`'s `getBudget(learnerId)`
 * so the SPA never handles a learner id.
 */
export const MeBudgetRoute = defineRoute({
  method: 'GET',
  path: '/api/account/me/budget',
  response: BudgetResponseSchema,
  description:
    "Return the authenticated learner's daily VM-time budget + upgrade URL.",
})
